import type { GanttFeature } from '@/components/kibo-ui/gantt'
import {
  GanttFeatureList,
  GanttFeatureRow,
  GanttHeader,
  GanttMarker,
  GanttProvider,
  GanttSidebar,
  GanttSidebarItem,
  GanttTimeline,
} from '@/components/kibo-ui/gantt'
import {
  ZOOM_MAX,
  ZOOM_MIN,
  nextZoom,
  prevZoom,
} from '@/components/kibo-ui/gantt/zoom'
import { Switch } from '@/components/ui/switch'
import { useActivityTypeLabels } from '@/modules/activities/use-activity-type-labels'
import type { SpecificConditionNode } from '@/modules/activities/types.graphql'
import type { VehicleColorDictionary } from '@/modules/activities/types'
import { useActivityColor } from '@/utils'
import { format } from '@/utils/date-fns'
import { durationAsSecondsToString } from '@/utils/time'
import { useTranslate } from '@ur-solutions/react-hooks'
import { addDays, differenceInSeconds, parseISO, startOfDay } from 'date-fns'
import { Minus, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import { useTheme } from 'styled-components'
import type { Activity, Infringement, Issue } from './activity-types'
import {
  ACTIVITY_LANE_ORDER,
  ACTIVITY_TYPE_BY_LANE,
  buildVehicleSegments,
  clampSpanToDay,
  laneOfActivityType,
} from './activity-lanes'
import type { ActivityLaneKey, DaySpan } from './activity-lanes'

const COMBINED_ROW_KEY = 'all-activities'
const VEHICLE_ROW_KEY = 'vehicle'

type GanttRow = {
  key: string
  label: string
  color?: string
  features: GanttFeature[]
}

type Pin = {
  id: string
  date: Date
  label: string
  color: string
  href?: string
}

type ActivitiesGanttProps = {
  activities: Activity[]
  date: Date
  infringements: Infringement[]
  issues: Issue[]
  specificConditions: SpecificConditionNode[]
  vehicleColors: VehicleColorDictionary
}

export const ActivitiesGantt: React.FC<ActivitiesGanttProps> = ({
  activities,
  date,
  infringements,
  issues,
  specificConditions,
  vehicleColors,
}) => {
  const translations = useTranslate({
    allActivities: 'activities.gantt-all-activities',
    combineRows: 'activities.gantt-combine-rows',
    ferryEnd: 'gantt.ferry-end',
    ferryEvent: 'infringements.ferry-event',
    ferryStart: 'gantt.ferry-start',
    infringement: 'common.infringement',
    issue: 'common.issue',
    nInfringements: ['common.n-infringements', { n: 0 }],
    nIssues: ['common.n-issues', { n: 0 }],
    nSpecificConditions: ['common.n-specific-conditions', { n: 0 }],
    noData: 'common.no-data',
    outOfScopeEnd: 'activities.out-of-scope-end',
    outOfScopeStart: 'activities.out-of-scope-start',
    unknown: 'common.unknown',
    vehicle: 'common.vehicle',
    zoomIn: 'common.zoom-in',
    zoomOut: 'common.zoom-out',
  })
  const navigate = useNavigate()
  const theme = useTheme()
  const getActivityColor = useActivityColor()
  const activityTypeLabels = useActivityTypeLabels()
  const [zoom, setZoom] = useState(() => (isMobile ? 30 : 60))
  const [combineRows, setCombineRows] = useState(false)

  // The chart is the day itself, midnight to midnight. Anything reaching past a
  // boundary is drawn cut off at it, and its bar carries only the part that
  // belongs to this day -- which is also what the row sums add up.
  const dayStart = useMemo(() => startOfDay(date), [date])
  const dayEnd = useMemo(() => addDays(dayStart, 1), [dayStart])

  const activityFeatures = useMemo(
    () =>
      activities.flatMap(activity => {
        const lane = laneOfActivityType(activity.activityType)
        if (!lane) return []

        const startAt = parseISO(activity.datetimeStart)
        const endAt = parseISO(activity.datetimeEnd)
        const span = clampSpanToDay(startAt, endAt, dayStart, dayEnd)
        if (!span) return []

        const label = activityTypeLabels[activity.activityType] ?? ''

        return [
          {
            lane,
            feature: {
              id: activity.id,
              name: '',
              startAt: span.start,
              endAt: span.end,
              duration: differenceInSeconds(span.end, span.start) / 60,
              group: {
                id: activity.activityType,
                name: label,
                color: getActivityColor(activity),
              },
              manuallyEntered: activity.manuallyEntered,
              tooltipContent: (
                <div>
                  <h3>
                    {label}{' '}
                    {durationAsSecondsToString(
                      differenceInSeconds(endAt, startAt)
                    )}
                  </h3>
                  <ul>
                    <li>{spanLabel(startAt, endAt, span)}</li>
                    {!!activity.vehicleRegistrationNumber && (
                      <li>{activity.vehicleRegistrationNumber}</li>
                    )}
                  </ul>
                </div>
              ),
            } satisfies GanttFeature,
          },
        ]
      }),
    [activities, activityTypeLabels, dayEnd, dayStart, getActivityColor]
  )

  const vehicleFeatures = useMemo(
    () =>
      buildVehicleSegments(activities).flatMap(segment => {
        const span = clampSpanToDay(
          segment.start,
          segment.end,
          dayStart,
          dayEnd
        )
        if (!span) return []

        const label = segment.registrationNumber ?? translations.unknown

        return [
          {
            id: segment.id,
            name: label,
            startAt: span.start,
            endAt: span.end,
            duration: differenceInSeconds(span.end, span.start) / 60,
            group: {
              id: segment.vehicleVin,
              name: label,
              color: vehicleColors[segment.vehicleVin] ?? theme.colors.gray5,
            },
            labelClassName:
              'font-bold text-white [text-shadow:0_1px_1px_rgb(0_0_0/0.35)]',
            tooltipContent: (
              <div>
                <h3>
                  {label}{' '}
                  {durationAsSecondsToString(
                    differenceInSeconds(segment.end, segment.start)
                  )}
                </h3>
                <ul>
                  <li>{spanLabel(segment.start, segment.end, span)}</li>
                  <li>{segment.vehicleVin}</li>
                </ul>
              </div>
            ),
          } satisfies GanttFeature,
        ]
      }),
    [
      activities,
      dayEnd,
      dayStart,
      theme.colors.gray5,
      translations.unknown,
      vehicleColors,
    ]
  )

  const rows = useMemo<GanttRow[]>(() => {
    const activityRows: GanttRow[] = combineRows
      ? [
          {
            key: COMBINED_ROW_KEY,
            label: translations.allActivities,
            features: activityFeatures.map(({ feature }) => feature),
          },
        ]
      : ACTIVITY_LANE_ORDER.map(lane => ({
          key: lane,
          label: activityTypeLabels[ACTIVITY_TYPE_BY_LANE[lane]] ?? lane,
          features: activityFeatures
            .filter(entry => entry.lane === lane)
            .map(({ feature }) => feature),
        })).filter(row => row.features.length > 0)

    // The vehicle row is the answer to "which truck was this?", so it stays on
    // screen in both modes even when the card is empty for the day.
    return [
      ...activityRows,
      {
        key: VEHICLE_ROW_KEY,
        label: translations.vehicle,
        features: vehicleFeatures,
      },
    ]
  }, [
    activityFeatures,
    activityTypeLabels,
    combineRows,
    translations.allActivities,
    translations.vehicle,
    vehicleFeatures,
  ])

  const specificConditionLabels = useMemo<Record<string, string>>(
    () => ({
      FERRY: translations.ferryEvent,
      FERRY_START: translations.ferryStart,
      FERRY_END: translations.ferryEnd,
      OUT_OF_SCOPE_START: translations.outOfScopeStart,
      OUT_OF_SCOPE_END: translations.outOfScopeEnd,
    }),
    [
      translations.ferryEnd,
      translations.ferryEvent,
      translations.ferryStart,
      translations.outOfScopeEnd,
      translations.outOfScopeStart,
    ]
  )

  // A pin registered outside the day has no place on the timeline to sit on.
  const pins = useMemo<Pin[]>(
    () =>
      [
        ...issues.map(issue => ({
          id: `issue-${issue.id}`,
          date: parseISO(issue.timeOfIssueStart),
          label: translations.issue,
          color: theme.colors.red,
          href: `/issues/${issue.id}`,
        })),
        ...infringements.map(infringement => ({
          id: `infringement-${infringement.id}`,
          date: parseISO(infringement.infringementTimestamp),
          label: translations.infringement,
          color: theme.colors.darkRed,
          href: `/infringements/${infringement.id}`,
        })),
        ...specificConditions.map(condition => ({
          id: `condition-${condition.id}`,
          date: parseISO(condition.timestamp),
          label: specificConditionLabels[condition.type] ?? condition.type,
          color: condition.type.startsWith('OUT')
            ? theme.colors.primary500
            : theme.colors.blue400,
        })),
      ].filter(
        pin =>
          pin.date.getTime() >= dayStart.getTime() &&
          pin.date.getTime() <= dayEnd.getTime()
      ),
    [
      dayEnd,
      dayStart,
      infringements,
      issues,
      specificConditionLabels,
      specificConditions,
      theme.colors.blue400,
      theme.colors.darkRed,
      theme.colors.primary500,
      theme.colors.red,
      translations.infringement,
      translations.issue,
    ]
  )

  const hasActivities = activityFeatures.length > 0

  return (
    <div className="flex w-full max-w-full flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Switch checked={combineRows} onCheckedChange={setCombineRows} />
          {translations.combineRows}
        </label>
        <div className="flex items-center gap-1 rounded-lg border border-gray-200">
          <button
            aria-label={translations.zoomOut}
            className="flex h-8 w-8 items-center justify-center text-gray-500 disabled:opacity-40"
            disabled={zoom <= ZOOM_MIN}
            onClick={() => setZoom(prevZoom)}
            type="button"
          >
            <Minus size={14} />
          </button>
          <span className="min-w-[36px] text-center text-[11px] font-semibold text-gray-900">
            {zoom}%
          </span>
          <button
            aria-label={translations.zoomIn}
            className="flex h-8 w-8 items-center justify-center text-gray-500 disabled:opacity-40"
            disabled={zoom >= ZOOM_MAX}
            onClick={() => setZoom(nextZoom)}
            type="button"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      {hasActivities ? (
        <GanttProvider
          className="rounded-lg border"
          datetimeEnd={dayEnd}
          datetimeStart={dayStart}
          range="daily"
          zoom={zoom}
        >
          <GanttSidebar className="!w-32 sm:!w-56" compact={isMobile}>
            {rows.map(row => (
              <GanttSidebarItem
                feature={{
                  id: row.key,
                  name: row.label,
                  startAt: row.features[0]?.startAt ?? dayStart,
                  endAt: row.features[row.features.length - 1]?.endAt ?? dayEnd,
                  duration: row.features.reduce(
                    (sum, feature) => sum + (feature.duration ?? 0),
                    0
                  ),
                }}
                key={row.key}
              />
            ))}
          </GanttSidebar>
          <GanttTimeline>
            <GanttHeader />
            {pins.map(pin => (
              <GanttMarker
                color={pin.color}
                date={pin.date}
                id={pin.id}
                key={pin.id}
                label={pin.label}
                onClick={pin.href ? () => void navigate(pin.href!) : undefined}
                tooltipContent={
                  <div>
                    <h3>{pin.label}</h3>
                    <ul>
                      <li>{format(pin.date, 'HH:mm')}</li>
                    </ul>
                  </div>
                }
              />
            ))}
            <GanttFeatureList>
              {rows.map(row => (
                <GanttFeatureRow
                  className="min-w-full"
                  features={row.features}
                  handleViewFeature={() => void 0}
                  key={row.key}
                />
              ))}
            </GanttFeatureList>
          </GanttTimeline>
        </GanttProvider>
      ) : (
        <div className="flex min-h-48 items-center justify-center rounded-lg border text-sm text-gray-500">
          {translations.noData}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        {ACTIVITY_LANE_ORDER.map(lane => (
          <LegendItem
            color={getActivityColor({
              activityType: ACTIVITY_TYPE_BY_LANE[lane],
            })}
            duration={laneDuration(activityFeatures, lane)}
            key={lane}
            label={activityTypeLabels[ACTIVITY_TYPE_BY_LANE[lane]] ?? lane}
          />
        ))}
        <LegendItem
          color={theme.colors.red}
          label={translations.nIssues({ n: issues.length })}
        />
        <LegendItem
          color={theme.colors.darkRed}
          label={translations.nInfringements({ n: infringements.length })}
        />
        <LegendItem
          color={theme.colors.blue400}
          label={translations.nSpecificConditions({
            n: specificConditions.length,
          })}
        />
      </div>
    </div>
  )
}

// A span cut at midnight keeps its real times in the tooltip, dated on the side
// that reaches out of the day so it is clear where the bar continues.
const spanLabel = (start: Date, end: Date, span: DaySpan) =>
  `${format(start, span.clippedStart ? 'dd.MM HH:mm' : 'HH:mm')} - ${format(
    end,
    span.clippedEnd ? 'dd.MM HH:mm' : 'HH:mm'
  )}`

const laneDuration = (
  entries: { lane: ActivityLaneKey; feature: GanttFeature }[],
  lane: ActivityLaneKey
) =>
  entries
    .filter(entry => entry.lane === lane)
    .reduce((sum, entry) => sum + (entry.feature.duration ?? 0), 0)

const LegendItem: React.FC<{
  color: string
  duration?: number
  label: string
}> = ({ color, duration, label }) => (
  <span className="flex items-center gap-2">
    <span
      className="inline-block h-2.5 w-2.5 rounded-full"
      style={{ backgroundColor: color }}
    />
    <span className="font-semibold">{label}</span>
    {duration !== undefined && (
      <span className="text-gray-500">
        {durationAsSecondsToString(duration * 60)}
      </span>
    )}
  </span>
)
